import FilterBox from "../../common/FilterBox";
import UserDirectoryFilter from "../../userDirectory/userDirectoryFilter/UserDirectoryFilter";
import { SearchBox } from "../../common/SearchBox";
import React, { useContext, useState } from "react";
import {
  IOrganizationContext,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import {
  FilterDataType,
  IDepartmentData,
  IUserInfo,
} from "@home-health-notify/domain";
import FilterTooltipText from "../../userDirectory/FilterTooltipText";
import { getFilterTooltipCount } from "../../userDirectory/UserDirectorySearch";
import { SearchAndFilterContainer } from "../AddMember.style";
import SelectedUserFilters from "../../common/SelectedUserFilters";

interface SearchAndFilterMembersProps {
  tabValue: number;
  onFinalUserDirectoryChange: (filteredDirectory: IUserInfo[]) => void;
  onResetUserDirectory: () => void;
  userDirectory: IUserInfo[];
}

export const SearchAndFilterUsers = ({
  tabValue,
  onFinalUserDirectoryChange,
  onResetUserDirectory,
  userDirectory,
}: SearchAndFilterMembersProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "userDirectory" });
  const tNChat = (key: string) => t(key, { ns: "chat" });
  const { selectedOrgUser }: IOrganizationContext =
    useContext(OrganizationContext);
  const [searchText, setSearchText] = useState("");
  const departments: IDepartmentData[] =
    selectedOrgUser?.getDepartments() || [];
  const shortHandDepartments = departments.map(
    (department: IDepartmentData) => department.shortHand,
  );
  const [filterTooltipData, setFilterTooltipData] = useState<FilterDataType>();
  const initialFilterData = {
    userRole: [],
    userType: [],
  };

  const [filterData, setFilterData] =
    useState<FilterDataType>(initialFilterData);
  const [filteredUserDirectory, setFilteredUserDirectory] = useState<
    IUserInfo[]
  >([]);

  const onSearchTextChange = (text: string) => {
    setSearchText(text);
    const directory =
      filterData.userRole.length > 0 || filterData.userType.length > 0
        ? filteredUserDirectory
        : userDirectory;
    const filteredUsers =
      selectedOrgUser?.userDirectory.searchByUserName(text, directory) || [];
    onFinalUserDirectoryChange(filteredUsers);
  };
  const onSearchClear = () => {
    setSearchText("");
    if (filterData.userRole.length === 0 && filterData.userType.length === 0) {
      onResetUserDirectory();
    } else {
      onFinalUserDirectoryChange(
        selectedOrgUser?.userDirectory.filterUserDirectory(filterData) || [],
      );
    }
  };

  const onFilter = () => {
    if (filterData.userRole.length > 0 || filterData.userType.length > 0) {
      const filteredUsers =
        selectedOrgUser?.userDirectory.filterUserDirectory(filterData) || [];
      setFilteredUserDirectory(filteredUsers);
      onFinalUserDirectoryChange(filteredUsers);

      if (searchText.length !== 0) {
        const matchedUsers =
          selectedOrgUser?.userDirectory.searchByUserName(
            searchText,
            filteredUsers,
          ) || [];
        onFinalUserDirectoryChange(matchedUsers);
      }
      setFilterTooltipData(filterData);
    } else {
      onResetUserDirectory();
      setFilterTooltipData(filterData);
    }
  };

  const onReset = () => {
    setFilterData(initialFilterData);
    setFilterTooltipData(undefined);
    if (searchText.length === 0) {
      onResetUserDirectory();
    } else {
      const filteredUsers =
        selectedOrgUser?.userDirectory.searchByUserName(
          searchText,
          userDirectory,
        ) || [];
      onFinalUserDirectoryChange(filteredUsers);
    }
  };

  return (
    <SearchAndFilterContainer>
      <SearchBox
        searchText={searchText}
        onSearchTextChange={onSearchTextChange}
        placeholderLabel={tNChat("search")}
        onSearchClear={onSearchClear}
        tabValue={tabValue}
      >
        {tabValue === 0 && (
          <FilterBox
            title={tNs("filter.title")}
            onApply={onFilter}
            onReset={onReset}
            filterCount={getFilterTooltipCount(filterTooltipData)}
            filterText={
              <FilterTooltipText filterTooltipData={filterTooltipData} />
            }
          >
            <UserDirectoryFilter
              departments={shortHandDepartments}
              filterData={filterData}
              setFilterData={setFilterData}
            />
          </FilterBox>
        )}
      </SearchBox>
      <SelectedUserFilters
        filters={filterTooltipData}
        filterCount={getFilterTooltipCount(filterTooltipData)}
      />
    </SearchAndFilterContainer>
  );
};
